import type { Thread } from "./mock-data";

/** Newest first, the same order as the server's pages. */
export const byNewest = (a: Thread, b: Thread) =>
  (b.sortKey ?? "").localeCompare(a.sortKey ?? "") || b.id.localeCompare(a.id);

/**
 * The thread list after a server refresh (polling, or an action
 * revalidating) brings a new first page: that page, then the threads already
 * scrolled in below it, newest first.
 *
 * The suggested draft is fetched per thread, not in the page, so it carries
 * over from the list as it was; a thread that changed has its draft fetched
 * again when it is open. `prepare` adds what else the page leaves out, such
 * as cached messages.
 */
export function mergeRefreshedPage(
  current: Thread[],
  page: Thread[],
  prepare: (thread: Thread) => Thread = (thread) => thread,
): Thread[] {
  const before = new Map(current.map((t) => [t.id, t]));
  const fresh = page.map((t) => {
    const old = before.get(t.id);
    return prepare(
      old ? { ...t, draft: old.draft, draftWriting: old.draftWriting } : t,
    );
  });
  const ids = new Set(fresh.map((t) => t.id));
  const below = current.filter((t) => !ids.has(t.id));
  return [...fresh, ...below].sort(byNewest);
}
